"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useLocalizedRouter as useRouter } from "@/lib/i18n/client";
import { Star } from "lucide-react";
import { fetchSimilarMovies, type MediaType, type MovieSearchResult } from "@/lib/api";
import { movieHref, titleKey } from "@/lib/movies";
import { useLocale, useMessages } from "@/lib/i18n/client";
import messages from "./similar-movies.i18n";

// "Títulos similares" se pide en el navegador y navega con router.push, sin
// <a href>, a propósito: como links en el HTML (o incluso en el DOM, porque
// Googlebot ejecuta JS) formaban una cadena casi infinita sobre el catálogo de
// TMDB que Googlebot recorría película por película. Cada película nueva era
// un render en una function de Netlify — agotó la cuota mensual (oct 2026).
// Una serie recomienda series y una película, películas.
export function SimilarMovies({ movieId, mediaType = "movie" }: { movieId: number; mediaType?: MediaType }) {
  const router = useRouter();
  const [movies, setMovies] = useState<MovieSearchResult[]>([]);
  const t = useMessages(messages);
  const lang = useLocale();

  useEffect(() => {
    fetchSimilarMovies(movieId, { mediaType, lang })
      .then(setMovies)
      .catch(() => setMovies([]));
  }, [movieId, mediaType, lang]);

  if (movies.length === 0) return null;

  return (
    <div className="mt-10">
      <h2 className="mb-3 text-sm font-medium text-muted-foreground">{t.title}</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
        {movies.map((similar) => (
          <button
            key={titleKey(similar)}
            type="button"
            onClick={() => router.push(movieHref(similar))}
            className="group text-left hover:cursor-pointer"
          >
            <div className="relative aspect-[2/3] overflow-hidden rounded-lg bg-muted">
              {similar.posterUrl && (
                <Image
                  src={similar.posterUrl}
                  alt={similar.title}
                  fill
                  sizes="150px"
                  className="object-cover transition group-hover:opacity-80"
                />
              )}
              {typeof similar.voteAverage === "number" && similar.voteAverage > 0 && (
                <div className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-background/85 px-1 py-0.5 text-[10px] font-semibold">
                  <Star className="h-2.5 w-2.5 fill-current text-amber-500" />
                  {similar.voteAverage.toFixed(1)}
                </div>
              )}
            </div>
            <p className="mt-1.5 line-clamp-2 text-xs font-medium">{similar.title}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
